import {useEffect, useMemo} from 'react';
import {
  Field,
  FieldDescription,
  FieldLabel,
  Input
} from '@kroll-resolver-crisp/ui-components-react';

type Props = {
  readonly file: File | null;
  readonly onFileSelected: (file: File) => void;
};

/** File picker + clipboard paste (Ctrl+V / Cmd+V) for the image-search investigation type. */
export function ImageUploadField({file, onFileSelected}: Props) {
  // Only mint a new blob URL when the file itself actually changes, not on every unrelated re-render
  // of the parent dialog (e.g. typing in the Intent field) - same pattern InvestigationsWorkspace uses
  // for the job image preview.
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

  useEffect(
    () => () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    },
    [previewUrl]
  );

  // No focused input needed for this — a window-level listener is the standard way to catch a
  // clipboard paste with no natural focus target (same rationale as the old standalone page).
  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const imageItem = [...(event.clipboardData?.items ?? [])].find((item) =>
        item.type.startsWith('image/')
      );
      const pastedFile = imageItem?.getAsFile();
      if (pastedFile) {
        event.preventDefault();
        onFileSelected(pastedFile);
      }
    };

    globalThis.addEventListener('paste', handlePaste);
    return () => {
      globalThis.removeEventListener('paste', handlePaste);
    };
  }, [onFileSelected]);

  return (
    <Field>
      <FieldLabel htmlFor="investigation-image">Image</FieldLabel>
      <div className="flex items-center gap-3">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Selected upload"
            className="border-border size-16 shrink-0 rounded-md border object-cover"
          />
        ) : null}
        {/* The native picker, not a hidden input relayed from a button: the browser names the
            chosen file itself, and the field is reachable by keyboard and screen reader as a
            file control rather than as a button that happens to open one. */}
        <Input
          id="investigation-image"
          type="file"
          accept="image/*"
          className="min-w-0 flex-1"
          onChange={(e) => {
            const selected = e.target.files?.[0];
            if (selected) {
              onFileSelected(selected);
            }
          }}
        />
      </div>
      {/* A pasted file never reaches the native input, which would still read "No file chosen",
          so the description names the file whichever way it arrived. */}
      <FieldDescription>
        {file
          ? `${file.name} selected. Choose another, or paste one (Ctrl+V / Cmd+V).`
          : 'Or paste an image (Ctrl+V / Cmd+V).'}
      </FieldDescription>
    </Field>
  );
}
